import { observer } from 'mobx-react-lite';
import {
  IEditServiceButtonFeatureViewModel,
  IEditServiceButtonFeatureViewModelToken
} from '@shared/types/mobx/features/osiAccruals';
import { useInjection } from 'inversify-react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slide,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  type MonthlyConfirmationService,
  MonthlyConfirmationServiceIdentifier
} from '@mobx/services/monthlyConfirmation';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { ServiceNameField } from '@features/osi/accruals/service/add/ui/ServiceNameField';
import { Controller, useForm } from 'react-hook-form';
import { Field } from '@shared/components/form/Field';
import React, { forwardRef, useCallback, useEffect } from 'react';
import { Icon } from '@iconify/react';
import editFill from '@iconify-icons/eva/edit-fill';
import {
  AccuralMethod,
  EditServiceFormValues,
  OsiServiceResponse,
  ServiceNameExample
} from '@shared/types/osi/services';
import { useTranslation } from '@shared/utils/i18n';
import { DeleteServiceButton } from '@features/osi/accruals/service/delete';
import { serviceSchema } from '@shared/validation/osi/services';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { ForceUpdateConfirmDialog } from '@features/osi/accruals/service/edit/ui/ForceUpdateConfirmDialog';

interface Props {
  service: OsiServiceResponse;
  accrualMethods: AccuralMethod[];
  defaultServiceNames: ServiceNameExample[];
  reloadCb: () => Promise<void>;
}

// @ts-expect-error slide not typed
const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export const EditServiceButton: React.FC<Props> = observer(
  ({ service, accrualMethods, defaultServiceNames, reloadCb }) => {
    const vm = useInjection<IEditServiceButtonFeatureViewModel>(IEditServiceButtonFeatureViewModelToken);
    // Источник planHasAccurals для услуги: store модалки RequiresMonthlyConfirmation.
    // Если услуга есть в pendingServices (значит у неё нет суммы в текущем месяце и она ждёт подтверждения),
    // берём её planHasAccurals — флаг «у текущего PlanAccural уже есть ACC».
    // Если услуги нет в pendingServices — кнопку «Начислить по услуге» не показываем.
    const monthlyVm = useInjection<MonthlyConfirmationService>(MonthlyConfirmationServiceIdentifier);
    // Признак ОСИ «Начисление пени»: чекбокс allowFines показываем только если пеня включена на уровне ОСИ
    const osiModule = useInjection<OsiService>(OsiServiceIdentifier);
    const createFine = osiModule.osiInfo?.createFine ?? false;
    // Фаза 3: для TARGET_PAY показываем AllowFines и TotalMonths
    const isTargetPay = service.serviceGroupCode === 'TARGET_PAY';
    // OSI-786: произвольная группа для показа чекбокса ежемесячного подтверждения. Гейтинг переключён с
    // code.startsWith('CUSTOM_') на флаг isCustom с бэка (уровень услуги), чтобы мигрированные группы
    // «Коммунальные/Котельная/Лифт» (code=NULL) тоже получили чекбокс requiresMonthlyConfirmation.
    const isCustomGroup = service.isCustom ?? false;

    const isCustomName = () => {
      const status = defaultServiceNames.some((defaultName) => service.nameRu === defaultName.nameRu);

      return !status;
    };

    const hookForm = useForm<EditServiceFormValues & { serviceId: number }>({
      defaultValues: {
        service: {
          code: isCustomName() ? 'custom' : 'default',
          nameRu: service.nameRu ?? '',
          nameKz: service.nameKz ?? ''
        },
        serviceGroupId: service.serviceGroupId,
        accrualMethodId: service.accuralMethodId,
        amount: service.amount,
        serviceId: service.id,
        // Фаза 3: дефолтные значения из существующей услуги
        allowFines: service.allowFines ?? true,
        totalMonths: service.totalMonths ?? null,
        // Ежемесячное подтверждение
        requiresMonthlyConfirmation: service.requiresMonthlyConfirmation ?? false
      },
      resolver: yupResolver(serviceSchema),
      mode: 'all'
    });
    const { t, fieldWithPrefix: fwp } = useTranslation();

    useEffect(() => {
      vm.setReloadCb(reloadCb);
    }, [reloadCb, vm]);

    useEffect(() => {
      vm.setHookForm(hookForm);
    }, [hookForm, vm]);

    const onEditServiceClick = () => {
      vm.onEditServiceClick(service, isCustomName());
    };

    const {
      control,
      handleSubmit,
      formState: { errors },
      watch
    } = hookForm;

    const accrualMethodId = watch('accrualMethodId', accrualMethods[0].id);
    // Текущее значение чекбокса requiresMonthlyConfirmation в форме (живое, не из props service):
    // нужно чтобы кнопка появлялась/исчезала прямо при переключении чекбокса.
    const requiresMonthlyConfirmation = watch(
      'requiresMonthlyConfirmation',
      service.requiresMonthlyConfirmation ?? false
    );

    const getLabel = useCallback(() => {
      const method = accrualMethods.find((method) => method.id === accrualMethodId);

      if (!method) return '';
      return fwp(method, 'description');
    }, [accrualMethodId, accrualMethods, fwp]);

    const { isOsiBilling } = service || false;

    // Кнопка «Начислить по услуге» видна только если выполнены ОБА условия:
    //  1) В форме включён requiresMonthlyConfirmation — иначе услуга начислится планово, доначисление не нужно;
    //  2) В текущем PlanAccural ОСИ уже есть ACC хотя бы по этой услуге (планового запуска уже не будет).
    // planHasAccurals для конкретной услуги берём из MonthlyConfirmationService.pendingServices
    // (там лежит результат GET /api/OsiServices/needs-monthly-confirmation, в котором это поле приходит).
    // Если услуги нет в pendingServices — значит сумма за месяц уже есть, доначислять нечего → кнопку прячем.
    const pendingForThisService = monthlyVm.pendingServices.find((s) => s.id === service.id);
    const planHasAccuralsForService = pendingForThisService?.planHasAccurals === true;
    const showRecreateButton = requiresMonthlyConfirmation === true && planHasAccuralsForService;

    return (
      <>
        {!isOsiBilling && (
          <IconButton onClick={onEditServiceClick} disabled={service.isOsiBilling}>
            <Icon icon={editFill} />
          </IconButton>
        )}
        {/* @ts-expect-error transition component not typed */}
        <Dialog open={vm.isDialogOpen} onClose={vm.closeDialog} TransitionComponent={Transition} fullWidth>
          <DialogTitle sx={{ mb: 2 }}>{t('accruals:editServiceTitle')}</DialogTitle>
          <DialogContent>
            <Box>
              <ServiceNameField
                control={control}
                name={'service'}
                options={defaultServiceNames}
                disabled={!isCustomName()}
              />
              <Controller
                control={control}
                name={'accrualMethodId'}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size={'small'}
                    select
                    fullWidth
                    label={t('accruals:accrualMethod')}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                    required
                    error={Boolean(errors?.accrualMethodId)}
                    helperText={errors?.accrualMethodId?.message ? t(errors.accrualMethodId.message) : ''}
                  >
                    {accrualMethods.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {fwp(method, 'description')}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Field
                control={control}
                name={'amount'}
                mask
                required
                fullWidth
                label={`${getLabel()}, ${t('common:tenge')}`}
                InputLabelProps={{ shrink: true }}
                type={'amount'}
                size={'small'}
                style={{ width: '100%' }}
                align="right"
                helperText={errors?.amount?.message ? t(errors.amount.message) : ''}
              />

              {/* Фаза 3: AllowFines — для TARGET_PAY по умолчанию выключен, можно включить.
                  Показываем только если на ОСИ включено «Начисление пени» (createFine) */}
              {isTargetPay && createFine && (
                <Controller
                  control={control}
                  name={'allowFines'}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('accruals:allowFines')}
                      sx={{ mt: 1, mb: 1, display: 'block' }}
                    />
                  )}
                />
              )}

              {/* Фаза 3: TotalMonths — количество месяцев начисления (для TARGET_PAY) */}
              {isTargetPay && (
                <Controller
                  control={control}
                  name={'totalMonths'}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === '' ? null : Number(val));
                      }}
                      size={'small'}
                      fullWidth
                      type="number"
                      label={t('accruals:totalMonths')}
                      InputLabelProps={{ shrink: true }}
                      helperText={t('accruals:totalMonthsHint')}
                      sx={{ mt: 1, mb: 1 }}
                      inputProps={{ min: 1 }}
                    />
                  )}
                />
              )}

              {/* Ежемесячное подтверждение — только для произвольных групп (без фиксированного code) */}
              {isCustomGroup && (
                <Controller
                  control={control}
                  name={'requiresMonthlyConfirmation'}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('accruals:requiresMonthlyConfirmation')}
                      sx={{ mt: 1, mb: 1, display: 'block' }}
                    />
                  )}
                />
              )}

            </Box>
          </DialogContent>
          <DialogActions>
            <DeleteServiceButton
              serviceId={service.id}
              content={service.nameRu ?? ''}
              reloadCb={reloadCb}
              onSuccess={vm.closeDialog}
            />
            <Button onClick={vm.closeDialog}>
              <Typography>{t('common:close')}</Typography>
            </Button>
            <Button
              variant={'contained'}
              disabled={!isEmpty(errors) || vm.isLoading}
              onClick={handleSubmit(vm.editService)}
            >
              <Typography>{t('common:save')}</Typography>
            </Button>
            {/* «Начислить по услуге» — сохраняет услугу + сразу дёргает POST /create-accurals-current-plan
                только для этой услуги. Видна только когда есть смысл доначислять (см. showRecreateButton). */}
            {showRecreateButton && (
              <LoadingButton
                variant={'contained'}
                color={'warning'}
                loading={vm.isLoading}
                disabled={!isEmpty(errors) || vm.isLoading}
                onClick={handleSubmit(vm.editServiceAndRecreate)}
              >
                <Typography>{t('accruals:recreateForService')}</Typography>
              </LoadingButton>
            )}
          </DialogActions>
        </Dialog>
        <ForceUpdateConfirmDialog
          open={vm.isForceUpdateConfirmDialogOpen}
          onClose={vm.closeForceUpdateConfirmDialog}
          onConfirm={vm.confirmForceUpdate}
          message={vm.forceUpdateMessage}
        />
      </>
    );
  }
);
